import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { initializeAuth } from '../../clerkAuth';
import { useClerkErrorHandler } from '../../clerkErrorHandling';
import { initializeApiErrorHandler } from '../../../api/authenticatedApi/clerkApiInterceptor';
import { LimitedOrganizationInfo, UserEntity } from '@kemu-io/kemu-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import { fetchUserProfileAction } from '@src/app/reducers/user/fetchUserReducer';
import { AsyncRequestStatus } from '@src/types/core_t';

export type UserSessionTokenContext = {
  user?: UserEntity | null; 
  organization?: LimitedOrganizationInfo | null; 
	state: 'LoggedIn' | 'NotLoggedIn';
}

export const UserSessionContext = createContext<UserSessionTokenContext>({
  user: null,
  // jwtToken: null,
  state: 'NotLoggedIn'
});


export const useAuthContext = () => {
  const context = useContext(UserSessionContext);
  if(!context) {
    throw new Error('useAuthContext must be used within an AuthContextProvider');
  }
  return context;
};

interface Props {
  children: React.ReactNode;
}

const UserSessionContextProvider = (props: Props): React.JSX.Element => {
  const auth = useAuth();
  const userProfile = useSelector(selectSignedUserProfile);
  const dispatch = useDispatch();
  const errorHandler = useClerkErrorHandler();
  const state = auth.isSignedIn ? 'LoggedIn' : 'NotLoggedIn';


  // Initialize auth instance and error handling for use in API calls
  useEffect(() => {
    if(auth.isLoaded){
      if(auth.isSignedIn){
        initializeAuth(auth);
        initializeApiErrorHandler(errorHandler);
      }
    }
  }, [auth, errorHandler]);

  useEffect(() => {
    if(auth.isSignedIn && auth.isLoaded && userProfile.asyncState.status === AsyncRequestStatus.idle){
      dispatch(fetchUserProfileAction());
    };
  }, [userProfile.asyncState.status, auth.isSignedIn, auth.isLoaded]);

  const contextValue = useMemo(() => ({
    user: userProfile.profile,
    organization: userProfile.organization,
    state: state as 'LoggedIn' | 'NotLoggedIn',
  }), [userProfile.profile, userProfile.organization, state]);
  
  return (
    <>
      <UserSessionContext.Provider value={contextValue}>
        {props.children}
      </UserSessionContext.Provider>
    </>
  );
};

export default UserSessionContextProvider;
